import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'token';
const REFRESH_KEY = 'refresh_token';
const DEVICE_KEY = 'device_id';

/** Identificativo stabile del dispositivo: serve a non richiedere il codice ogni volta. */
const getDeviceId = () => {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
};
const REFRESH_EVERY_MS = 6 * 60 * 60 * 1000; // rinnovo proattivo ogni 6h di app aperta

// Il token vive in localStorage quando l'utente resta connesso (sopravvive a chiusura
// scheda/browser e allo scarico della pagina da parte di iOS), in sessionStorage altrimenti.
const readToken = () => localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
const readRefresh = () => localStorage.getItem(REFRESH_KEY);

const storeSession = (accessToken, refreshToken) => {
  if (refreshToken) {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_KEY, refreshToken);
    sessionStorage.removeItem(TOKEN_KEY);
  } else {
    sessionStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  }
};

const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(readToken);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(token);
  const refreshingRef = useRef(null);

  useEffect(() => { tokenRef.current = token; }, [token]);

  const logout = useCallback(async () => {
    const refreshToken = readRefresh();
    clearSession();
    setToken(null);
    setUser(null);
    if (refreshToken) {
      try { await axios.post(`${API}/auth/logout`, { refresh_token: refreshToken }); } catch { /* best effort */ }
    }
  }, []);

  // Rinnova l'accesso con il refresh token (una sola richiesta anche con chiamate parallele)
  const refreshSession = useCallback(async () => {
    const refreshToken = readRefresh();
    if (!refreshToken) return null;
    if (!refreshingRef.current) {
      refreshingRef.current = axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken })
        .then(res => {
          const { access_token, refresh_token, user: userData } = res.data;
          storeSession(access_token, refresh_token);
          tokenRef.current = access_token;
          setToken(access_token);
          if (userData) setUser(userData);
          return access_token;
        })
        .catch(err => {
          if (err.response?.status === 401) {
            clearSession();
            setToken(null);
            setUser(null);
          }
          return null;
        })
        .finally(() => { refreshingRef.current = null; });
    }
    return refreshingRef.current;
  }, []);

  // Un token scaduto non deve buttare fuori l'utente: si prova prima il rinnovo silenzioso
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      res => res,
      async error => {
        const config = error.config || {};
        const isAuthCall = (config.url || '').includes('/auth/refresh') || (config.url || '').includes('/auth/login');
        if (error.response?.status !== 401 || config._retried || isAuthCall || !readRefresh()) {
          return Promise.reject(error);
        }
        const fresh = await refreshSession();
        if (!fresh) return Promise.reject(error);
        config._retried = true;
        config.headers = { ...(config.headers || {}), Authorization: `Bearer ${fresh}` };
        return axios(config);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [refreshSession]);

  useEffect(() => {
    const initAuth = async () => {
      const current = readToken();
      if (!current) { setLoading(false); return; }
      try {
        const response = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${current}` } });
        setUser(response.data);
      } catch {
        const fresh = await refreshSession();
        if (fresh) {
          try {
            const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${fresh}` } });
            setUser(res.data);
          } catch { clearSession(); setToken(null); }
        } else {
          clearSession();
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rinnovo proattivo: al ritorno sull'app e periodicamente, così la sessione non scade mai durante l'uso
  useEffect(() => {
    if (!token) return;
    let last = Date.now();
    const maybeRefresh = () => {
      if (document.visibilityState === 'visible' && Date.now() - last > REFRESH_EVERY_MS) {
        last = Date.now();
        refreshSession();
      }
    };
    const timer = setInterval(maybeRefresh, 15 * 60 * 1000);
    document.addEventListener('visibilitychange', maybeRefresh);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', maybeRefresh); };
  }, [token, refreshSession]);

  const login = useCallback(async (email, password, rememberMe = true) => {
    const response = await axios.post(`${API}/auth/login`, {
      email, password, remember_me: rememberMe, device_id: getDeviceId(),
    });
    if (response.data?.verification_required) {
      // Dispositivo nuovo: la sessione parte solo dopo il codice ricevuto via email
      if (response.data.device_id) localStorage.setItem(DEVICE_KEY, response.data.device_id);
      return { verification: response.data };
    }
    const { access_token, refresh_token, user: userData } = response.data;
    storeSession(access_token, refresh_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const verifyDevice = useCallback(async (challengeId, code, rememberMe = true) => {
    const response = await axios.post(`${API}/auth/verify-device`, {
      challenge_id: challengeId, code, remember_me: rememberMe,
    });
    const { access_token, refresh_token, user: userData } = response.data;
    storeSession(access_token, refresh_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const resendDeviceCode = useCallback(async (challengeId) => {
    const res = await axios.post(`${API}/auth/resend-device-code`, { challenge_id: challengeId });
    return res.data;
  }, []);

  const register = useCallback(async (email, password, name, role) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    const { access_token, refresh_token, user: userData } = response.data;
    storeSession(access_token, refresh_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const [actAs, setActAs] = useState(null);

  const getAuthHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}`, ...(actAs ? { 'X-Act-As': actAs } : {}) }
  }), [token, actAs]);

  const value = useMemo(() => ({
    user, token, loading, login, register, logout, getAuthHeaders, API, actAs, setActAs,
    verifyDevice, resendDeviceCode,
  }), [user, token, loading, login, register, logout, getAuthHeaders, actAs, verifyDevice, resendDeviceCode]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
